import React, { useContext, useReducer } from "react";
import { useNavigate } from "react-router-dom";
import IconButton from "@mui/material/IconButton";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import EditIcon from "@mui/icons-material/Edit";

// Custom.
import Layout from "containers/Layout/Layout";
import { Form } from "@vocollege/components";
import I18n from "@vocollege/app/dist/modules/Services/I18n";
import {
  GET_AVAILABLE_GROUPS,
  GET_GROUP,
  UPDATE_GROUP,
  CREATE_GROUP,
} from "@vocollege/app/dist/modules/VoApi/graphql";
import { useParams } from "react-router-dom";
import { FormFieldContentListItem } from "@vocollege/components/dist/Form";
import { regexPatterns } from "@vocollege/app";
import { GeneralObject } from "@vocollege/app/dist/global";
import { SEARCH_USER } from "@vocollege/app/dist/modules/VoApi";
import { getUserString, reducer } from "@vocollege/app/dist/modules/VoHelpers";
import GroupUserEdit from "./GroupUserEdit";
import EnhancedTableRoles from "@vocollege/components/dist/EnhancedTable/components/EnhancedTableRoles";
import { VoAppContext } from "services/GlobalState";
import { FormTabProps } from "@vocollege/components/dist/Form/global";
import VoLoader from "@vocollege/components/dist/VoLoader";
import { getInitialState, getInitialFormState } from "./state";
import EducationDialog from "components/EducationDialog";
import GroupRetention from "./GroupRetention";
import VoAuth from "@vocollege/app/dist/modules/VoAuth";

export default function GroupEdit() {
  const params = useParams<any>();
  const navigate = useNavigate();
  const { state: globalState } = useContext(VoAppContext);
  const [state, dispatch] = useReducer(reducer, getInitialState());

  // Methods.

  const handleNameChange = (data: any) => {
    // setUrlValue(data);
    dispatch({ key: "urlValue", values: data });
  };

  const handleUsersChange = (data: any) => {
    if (state.group?.users.length === data.length) {
      return;
    }
    dispatch({ key: "group.users", values: data });
  };

  const handleEducationsChange = (data: any) => {
    if (state.group?.educations?.length === data.length) {
      return;
    }
    dispatch({ key: "group.educations", values: data });
  };

  const getAvailableCategoryValues = () => {
    return Object.keys(I18n.get.group.categories).map((category: string) => ({
      label: I18n.get.group.categories[category],
      value: category,
    }));
  };

  const getAvailableRegionValues = (data: any) => {
    if (!data || !data?.allGroups) {
      return [];
    }
    return data.allGroups
      .filter((v: any) => params.id === "create" || v.id !== params.id)
      .map((v: any) => ({
        title: v.title,
        id: v.id,
      }));
  };

  const getAvailableEducatorTypeValues = (data: any) => {
    return Object.keys(I18n.get.group.educatorTypes).map((type: string) => ({
      label: I18n.get.group.educatorTypes[type],
      value: type,
    }));
  };

  const getEducatorTypeValue = (selected: any) => {
    return selected
      .map((type: string) => I18n.get.group.educatorTypes[type])
      .join(" | ");
  };

  const getAvailableEmployerTypeValues = (data: any) => {
    return Object.keys(I18n.get.group.employerTypes).map((type: string) => ({
      label: I18n.get.group.employerTypes[type],
      value: type,
    }));
  };

  const getEmployerTypeValue = (selected: any) => {
    return selected
      .map((type: string) => I18n.get.group.employerTypes[type])
      .join(" | ");
  };

  const getAvailableEducatorCategoryValues = (data: any) => {
    return Object.keys(I18n.get.group.educatorCategories).map(
      (category: string) => ({
        label: I18n.get.group.educatorCategories[category],
        value: category,
      })
    );
  };

  const getEducatorCategoryValue = (selected: any) => {
    return selected
      .map((category: string) => I18n.get.group.educatorCategories[category])
      .join(" | ");
  };

  const handleRedirect = () => {
    if (!globalState.group) {
      navigate("/groups");
    } else {
      // setReloading(true);
      dispatch({ key: "reloading", values: true });
      window.setTimeout(() => {
        // setReloading(false);
        dispatch({ key: "reloading", values: false });
      }, 500);
    }
  };

  const getEducationsCustomActionButtons = (item: FormFieldContentListItem) => {
    return (
      <IconButton
        aria-label="edit"
        onClick={() => dispatch({ key: "selectedEducation", values: item })}>
        <EditIcon />
      </IconButton>
    );
  };

  const getEducationsCustomDetails = (item: FormFieldContentListItem) => {
    let education = state.group?.educations.find((v: any) => v.id === item.id);
    if (!education) {
      return <></>;
    }
    return (
      <Box sx={{ mt: 0.5 }}>
        {education?.pivot?.certified && (
          <Typography variant="caption" component="h6">
            <strong>{I18n.get.form.labels.certified}:</strong>{" "}
            {education.pivot.certified || "-"}
          </Typography>
        )}
        {education?.pivot?.comments && education.pivot.comments !== "" && (
          <Typography variant="caption" component="h6">
            <strong>{I18n.get.form.labels.comments}:</strong>{" "}
            {education.pivot.comments}
          </Typography>
        )}
      </Box>
    );
  };

  const getUsersCustomActionButtons = (item: FormFieldContentListItem) => {
    return (
      // <IconButton aria-label="edit" onClick={() => setSelectedUser(item)}>
      <IconButton
        aria-label="edit"
        onClick={() => dispatch({ key: "selectedUser", values: item })}>
        <EditIcon />
      </IconButton>
    );
  };

  const getUsersCustomDetails = (item: FormFieldContentListItem) => {
    let user = state.group?.users.find((v: any) => v.id === item.id);
    if (!user) {
      return <></>;
    }
    return <EnhancedTableRoles data={user.groupRoles} />;
  };

  const handleGroupUserSave = (data: GeneralObject) => {
    // setSelectedUser(null);
    dispatch({ key: "selectedUser", values: null });
    let changedUsers = [...state.group?.users];
    let i = changedUsers.findIndex((v: any) => v.id === data.id);
    if (i === -1) {
      return;
    }
    let changedUser = { ...changedUsers[i] };
    changedUser.groupRoles = [...data.groupRoles];
    changedUsers[i] = changedUser;
    // setUsers(changedUsers);
    dispatch({ key: "group.users", values: changedUsers });
  };

  const handleGroupEducationSave = (data: GeneralObject) => {
    dispatch({ key: "selectedEducation", values: null });
    let changedEducations = [...state.group?.educations];
    let i = changedEducations.findIndex((v: any) => v.id === data.id);
    if (i === -1) {
      return;
    }
    let changedEducation = { ...changedEducations[i], pivot: data.pivot };
    changedEducations[i] = changedEducation;
    dispatch({ key: "group.educations", values: changedEducations });
  };

  const getTabs = () => {
    const tabs: FormTabProps[] = [];

    if (!globalState.group) {
      tabs.push({
        key: "general",
        label: I18n.get.misc.general,
        fields: [
          {
            name: "name",
            label: I18n.get.form.labels.name,
            type: "text",
            required: true,
            grid: {
              xs: 12,
            },
            validation: {
              required: true,
              pattern: {
                value: regexPatterns.cleanName,
                message: I18n.get.group.errors.invalidGroupName,
              },
            },
            onChange: handleNameChange,
          },
          {
            name: "title",
            label: I18n.get.form.labels.title,
            type: "text",
            required: true,
            grid: {
              xs: 12,
              md: 9,
            },
            validation: {
              required: true,
            },
          },
          {
            name: "status",
            label: I18n.get.status.published,
            type: "switch",
            required: true,
            grid: {
              xs: 12,
              md: 3,
            },
          },
          {
            name: "description",
            label: I18n.get.form.labels.description,
            type: "text",
            grid: {
              xs: 12,
              md: 9,
            },
          },
          {
            name: "certified",
            label: I18n.get.form.labels.certified,
            type: "date_field",
            grid: {
              xs: 12,
              md: 3,
            },
          },
          {
            name: "category",
            label: I18n.get.form.labels.category,
            type: "select",
            required: true,
            grid: {
              xs: 12,
              md: 9,
            },
            validation: {
              required: true,
            },
            params: {
              availableValues: getAvailableCategoryValues,
            },
            // onChange: handleCategoryChange,
            onChange: (value, data) => {
              dispatch({
                key: "group",
                values: { ...state.group, category: value },
              });
            },
          },
          {
            name: "decertified",
            label: I18n.get.form.labels.decertified,
            type: "date_field",
            grid: {
              xs: 12,
              md: 3,
            },
          },
          {
            name: "parentGroups",
            label: I18n.get.group.labels.groupAffiliation,
            type: "autocomplete",
            grid: {
              xs: 12,
            },
            params: {
              AutocompleteProps: {
                multiple: true,
                getOptionLabel: (option: GeneralObject) =>
                  option ? option.title : "",
                isOptionEqualToValue: (
                  option: GeneralObject,
                  value: GeneralObject
                ) => value.id === option.id,
              },
              TextFieldProps: {
                helperText: I18n.get.group.messages.parentGroupsHelper,
              },
              options: getAvailableRegionValues,
              dependency: {
                category: {
                  value: ["region"],
                  visibility: false,
                },
              },
            },
          },
          {
            name: "employer_type",
            label: I18n.get.group.labels.employerType,
            type: "select",
            grid: {
              xs: 12,
            },
            params: {
              multiple: true,
              availableValues: getAvailableEmployerTypeValues,
              renderValue: getEmployerTypeValue,
              dependency: {
                category: {
                  value: ["employer"],
                  visibility: true,
                },
              },
            },
          },

          {
            name: "location",
            label: I18n.get.form.labels.location,
            type: "location",
            // required: true,
            grid: {
              xs: 12,
            },
          },

          {
            name: "contacts",
            label: I18n.get.group.labels.contacts,
            type: "content_list",
            grid: {
              xs: 12,
            },
            params: {
              dialog: {
                types: ["card"],
              },
            },
          },
        ],
      });
    }

    if (!globalState.group) {
      const visible = state.group?.category === "educator";
      tabs.push({
        key: "educations",
        label: I18n.get.group.categories.educator,
        visible: visible,
        fields: [
          {
            name: "educator_category",
            label: I18n.get.group.labels.educatorCategory,
            type: "select",
            grid: {
              xs: 12,
            },
            params: {
              multiple: true,
              availableValues: getAvailableEducatorCategoryValues,
              renderValue: getEducatorCategoryValue,
              helperText: I18n.get.group.messages.doNotUseOldCategories,
              disabled: true,
              dependency: {
                category: {
                  value: ["educator"],
                  visibility: true,
                },
              },
            },
          },
          {
            name: "educator_type",
            label: I18n.get.group.labels.educatorType,
            type: "select",
            grid: {
              xs: 12,
            },
            params: {
              multiple: true,
              availableValues: getAvailableEducatorTypeValues,
              renderValue: getEducatorTypeValue,
              dependency: {
                category: {
                  value: ["educator"],
                  visibility: true,
                },
              },
            },
          },
          {
            name: "educations",
            label: I18n.get.education.labels.educations,
            type: "content_list",
            grid: {
              xs: 12,
            },
            overrideValue:
              state.group?.educations?.length > 0
                ? state.group?.educations
                : null,
            onChange: handleEducationsChange,
            params: {
              dialog: {
                types: ["education"],
              },
              renderItemTitle: (item: any) => `${item.id} | ${item.title}`,
              renderActionButtons: getEducationsCustomActionButtons,
              renderExtraDetails: getEducationsCustomDetails,
              hideType: true,
            },
          },
        ],
      });
    }

    tabs.push({
      key: "vopage",
      label: `${I18n.get.group.labels.vopage} / ${I18n.get.group.labels.startPage}`,
      fields: [
        {
          name: "grade_warning",
          label: "",
          type: "custom",
          grid: {
            xs: 12,
          },
          render: (data: any) => (
            <Alert severity="warning">
              {I18n.get.group.messages.frontPageIntro}
            </Alert>
          ),
        },
        {
          name: "vopage",
          label: I18n.get.group.labels.vopagesStatus,
          type: "switch",
          grid: {
            xs: 12,
          },
          params: {
            boolean: true,
            helperText: I18n.get.group.messages.vopagesStatus,
          },
        },
        {
          name: "front_title",
          label: I18n.get.form.labels.title,
          type: "text",
          grid: {
            xs: 12,
            md: 8,
          },
        },
        {
          name: "data_protection_policy_url",
          label: I18n.get.form.labels.dataProtectionPolicyUrl,
          type: "text",
          grid: {
            xs: 12,
            md: 4,
          },
          params: {
            helperText: I18n.get.form.messages.dataProtectionPolicyUrlHelper,
          },
        },
        {
          name: "images",
          label: I18n.get.misc.image,
          type: "file_field",
          grid: {
            xs: 12,
          },
          params: {
            filetypes: ["image"],
            multiple: false,
          },
        },
        {
          name: "front_body",
          label: I18n.get.form.labels.body,
          type: "editor",
          grid: {
            xs: 12,
          },
        },
      ],
    });

    if (!globalState.group) {
      tabs.push({
        key: "page",
        label: I18n.get.page.label,
        fields: [
          {
            name: "url",
            label: I18n.get.form.labels.url,
            type: "url_field",
            grid: {
              xs: 12,
            },
            overrideValue: state.urlValue,
            params: {
              dependencyHelperText: I18n.trans(
                I18n.get.form.messages.fieldPopulatedBy,
                { field: I18n.get.form.labels.name }
              ),
            },
          },
          {
            name: "docs",
            label: I18n.get.misc.documentbank,
            type: "text",
            grid: {
              xs: 12,
              md: 6,
            },
          },
          {
            name: "website",
            label: I18n.get.form.labels.website,
            type: "text",
            grid: {
              xs: 12,
              md: 6,
            },
          },
          {
            name: "body",
            label: I18n.get.form.labels.body,
            type: "editor",
            grid: {
              xs: 12,
            },
          },
        ],
      });
    }

    if (!globalState.group) {
      const categoryIsRegion = state.group?.category === "region";
      tabs.push({
        key: "logotypes",
        label: I18n.get.misc.logotypes,
        fields: [
          {
            name: "logo",
            label: I18n.get.misc.logotype,
            type: "file_field",
            required: categoryIsRegion,
            validation: {
              required: categoryIsRegion,
            },
            grid: {
              xs: 12,
            },
            params: {
              filetypes: ["image"],
              multiple: false,
            },
          },
          {
            name: "logo2",
            label: `${I18n.get.misc.logotype} 2`,
            type: "file_field",
            required: categoryIsRegion,
            validation: {
              required: categoryIsRegion,
            },
            grid: {
              xs: 12,
            },
            params: {
              filetypes: ["image"],
              multiple: false,
            },
          },
          {
            name: "logo3",
            label: `${I18n.get.misc.logotype} 3`,
            type: "file_field",
            required: categoryIsRegion,
            validation: {
              required: categoryIsRegion,
            },
            grid: {
              xs: 12,
            },
            params: {
              filetypes: ["image"],
              multiple: false,
            },
          },
        ],
      });
    }

    tabs.push({
      key: "users",
      label: I18n.get.user.labels.users,
      fields: [
        {
          name: "users",
          label: I18n.get.user.labels.users,
          type: "content_list",
          grid: {
            xs: 12,
          },
          overrideValue:
            state.group?.users?.length > 0 ? state.group?.users : null,
          onChange: handleUsersChange,
          params: {
            renderItemTitle: (item: any) =>
              `${item.id} | ${getUserString(item)}`,
            renderActionButtons: getUsersCustomActionButtons,
            renderExtraDetails: getUsersCustomDetails,
            hideType: true,
            dialog: {
              renderTitleField: (item: any) => getUserString(item),
              query: SEARCH_USER,
              category: "searchUser",
            },
          },
        },
      ],
    });

    if (!globalState.group && params.id && params.id !== "create" &&
        VoAuth.ability.can("delete", "Users") && VoAuth.ability.can("update", "Groups")) {
      tabs.push({ key: "retention", label: "Avtalsavslut", fields: [{
        name: "retention_action", label: "", type: "custom", grid: { xs: 12 },
        render: () => <GroupRetention key={params.id} id={String(params.id)} />,
      }] });
    }
    return tabs;
  };

  if (state.reloading) {
    return <VoLoader />;
  }

  return (
    <Layout>
      <Form
        tabs={getTabs()}
        labels={{
          loading: I18n.get.form.labels.loadingContent,
          create: I18n.get.form.labels.createGroup,
          created: I18n.get.group.messages.groupSaved,
          updated: I18n.get.group.messages.groupSaved,
          deleted: I18n.get.group.messages.groupDeleted,
          fieldRequired: I18n.get.form.errors.required,
        }}
        operations={{
          category: "group",
          get: GET_GROUP,
          create: CREATE_GROUP,
          update: UPDATE_GROUP,
          getOnCreate: GET_AVAILABLE_GROUPS,
        }}
        variables={{ categories: ["region", "local"] }}
        initialState={getInitialFormState()}
        pageTitleField="title"
        classes={{
          paper: "vo-global__content-with-toolbar",
          toolbar: "vo-global__content-toolbar",
        }}
        onComplete={handleRedirect}
        onCancel={handleRedirect}
        onDataChange={(data, fetchedData) => {
          dispatch({ key: "group", values: data });
        }}
      />
      {state.selectedUser && (
        <GroupUserEdit
          user={state.selectedUser}
          onClose={() => dispatch({ key: "selectedUser", values: null })}
          onSave={handleGroupUserSave}
        />
      )}
      {state.selectedEducation && (
        <EducationDialog
          item={state.selectedEducation}
          onClose={() => dispatch({ key: "selectedEducation", values: null })}
          onSave={handleGroupEducationSave}
        />
      )}
    </Layout>
  );
}
